import type { JSX } from 'react';
import { useState } from 'react';
import { Container } from '@components/ui/Container';
import { FAQItem } from '@components/ui/FAQItem';
import { RevealOnView } from '@components/ui/RevealOnView';
import { SectionHeading } from '@components/ui/SectionHeading';
import { landingCopy } from '@features/landing/data/landing.pt';

/**
 * Secao "FAQ" (SECT-07 — PRD §4.8).
 *
 * Apresenta 6 perguntas em accordion (UI-03 `FAQItem`). Comportamento:
 *  - Apenas UMA aberta por vez (PRD §4.8): clicar uma fechada fecha a aberta
 *    e abre a clicada; clicar a aberta fecha tudo.
 *  - Estado gerenciado por indice (`openIndex`) — `null` quando nenhuma aberta.
 *  - Toggle por click ou Enter/Space (nativo do button).
 *
 * Background: `bg-white` (alternancia depois da banda Diferenciais
 * `bg-brand-900` — volta pro ritmo light antes do CTA Final).
 * Anchor: `#faq` (acessado pelo menu FAQ do Navbar e pelo Footer nav).
 *
 * Reveal: SectionHeading + cada FAQItem em stagger.
 */
export const Faq = (): JSX.Element => {
  const { faq } = landingCopy;
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleAt = (index: number): (() => void) => {
    return () => {
      setOpenIndex((current) => (current === index ? null : index));
    };
  };

  return (
    <section id="faq" aria-labelledby="faq-heading" className="bg-white py-20 lg:py-28">
      <Container className="max-w-4xl">
        <RevealOnView>
          <SectionHeading
            as="h2"
            eyebrow={faq.eyebrow}
            title={faq.title}
            align="left"
            titleId="faq-heading"
          />
        </RevealOnView>

        <div className="mt-12 border-t border-neutral-200">
          {faq.entries.map((entry, index) => (
            <RevealOnView key={entry.question} delayMs={80 * (index + 1)} slide="none">
              <FAQItem
                question={entry.question}
                answer={entry.answer}
                isOpen={openIndex === index}
                onToggle={toggleAt(index)}
              />
            </RevealOnView>
          ))}
        </div>
      </Container>
    </section>
  );
};
